import * as React from "react";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useQuery, useMutation } from "react-query";
import {
  ApplicationEventSubscription,
  ApplicationEventSubscriptionsService,
  ErrorResponse,
  SystemSettings,
  SystemSettingsService,
  Branch
} from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableColumn, DataTableRowAction } from "components/DataTable/types";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { useDialogState } from "hooks/useDialogState";
import { CreateEventSubscriptionDialog } from "./CreateEventSubscriptionDialog";
import { EditEventSubscriptionDialog } from "./EditEventSubscriptionDialog";
import { describeApplicationEventType } from "utils/applicationEventUtils";

const EventSubscriptionsTab: React.FC = () => {
  const [isCreateDialogOpen, openCreateDialog, closeCreateDialog] = useDialogState();
  const [isEditDialogOpen, openEditDialog, closeEditDialog] = useDialogState();
  const [isDeleteDialogOpen, openDeleteDialog, closeDeleteDialog] = useDialogState();
  const [selectedSubscription, setSelectedSubscription] = React.useState<ApplicationEventSubscription | null>(null);

  // Fetch system settings to get branches
  const { data: systemSettings } = useQuery<SystemSettings, ErrorResponse>(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const branches = React.useMemo(() => {
    return systemSettings?.lltsBranches || [];
  }, [systemSettings?.lltsBranches]);

  // Create a map for quick branch lookup
  const branchMap = React.useMemo(() => {
    const map = new Map<string, Branch>();
    branches.forEach(branch => {
      map.set(branch.id, branch);
    });
    return map;
  }, [branches]);

  const { data, isLoading, error, refetch } = useQuery<
    { items: ApplicationEventSubscription[]; nextToken?: string },
    ErrorResponse
  >(
    ["listEventSubscriptions", "ALL"],
    () => ApplicationEventSubscriptionsService.listApplicationEventSubscriptions({ clientId: "ALL" }),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  const deleteMutation = useMutation(
    (subscriptionId: string) =>
      ApplicationEventSubscriptionsService.deleteApplicationEventSubscription({ subscriptionId }),
    {
      onSuccess: () => {
        refetch();
        closeDeleteDialog();
        setSelectedSubscription(null);
      }
    }
  );

  const handleEditClick = React.useCallback(
    (subscription: ApplicationEventSubscription) => {
      setSelectedSubscription(subscription);
      openEditDialog();
    },
    [openEditDialog]
  );

  const handleDeleteClick = React.useCallback(
    (subscription: ApplicationEventSubscription) => {
      setSelectedSubscription(subscription);
      openDeleteDialog();
    },
    [openDeleteDialog]
  );

  const handleCreateSuccess = React.useCallback(() => {
    refetch();
    closeCreateDialog();
  }, [refetch, closeCreateDialog]);

  const handleEditSuccess = React.useCallback(() => {
    refetch();
    closeEditDialog();
    setSelectedSubscription(null);
  }, [refetch, closeEditDialog]);

  const handleDeleteConfirm = React.useCallback(() => {
    if (selectedSubscription) {
      deleteMutation.mutate(selectedSubscription.id);
    }
  }, [selectedSubscription, deleteMutation]);

  const getBranchName = React.useCallback(
    (branchId: string) => {
      if (branchId === "ALL") {
        return "All";
      }
      const branch = branchMap.get(branchId);
      return branch?.name || branchId;
    },
    [branchMap]
  );

  const columns: DataTableColumn[] = [
    {
      id: "eventType",
      title: <strong>Event Type</strong>,
      cell: (row: ApplicationEventSubscription) => describeApplicationEventType(row.eventType)
    },
    {
      id: "branch",
      title: <strong>Branch</strong>,
      cell: (row: ApplicationEventSubscription) => getBranchName(row.branchId)
    },
    {
      id: "emails",
      title: <strong>Notification Emails</strong>,
      cell: (row: ApplicationEventSubscription) => row.emails.join(", ")
    },
    {
      id: "notes",
      title: <strong>Notes</strong>,
      cell: (row: ApplicationEventSubscription) => row.notes || "—"
    }
  ];

  const rowActions = React.useCallback(
    (row: ApplicationEventSubscription): DataTableRowAction[] => [
      {
        type: "ACTION",
        title: "Edit",
        action: () => handleEditClick(row)
      },
      {
        type: "ACTION",
        title: "Delete",
        action: () => handleDeleteClick(row),
        color: "error.main"
      }
    ],
    [handleEditClick, handleDeleteClick]
  );

  const subscriptions = React.useMemo(() => {
    return (data?.items || []).sort((a, b) => a.eventType.localeCompare(b.eventType));
  }, [data?.items]);

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Event Subscriptions</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          Add Subscription
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <ApiErrorMessage apiError={error} />
        </Box>
      )}

      <DataTable
        data={subscriptions}
        rowKey={(row: ApplicationEventSubscription) => row.id}
        columns={columns}
        rowActions={rowActions}
        isLoading={isLoading}
        dense
        noData={
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No event subscriptions found. Click "Add Subscription" to create one.
            </Typography>
          </Box>
        }
      />

      <CreateEventSubscriptionDialog
        open={isCreateDialogOpen}
        onClose={closeCreateDialog}
        branches={branches}
        onSuccess={handleCreateSuccess}
      />

      {selectedSubscription && (
        <EditEventSubscriptionDialog
          open={isEditDialogOpen}
          onClose={closeEditDialog}
          subscription={selectedSubscription}
          branches={branches}
          onSuccess={handleEditSuccess}
        />
      )}

      {isDeleteDialogOpen && (
        <ConfirmationDialog
          title="Delete Event Subscription"
          content="Are you sure you want to delete this event subscription? This action cannot be undone."
          onClose={closeDeleteDialog}
          onConfirm={handleDeleteConfirm}
          isLoading={deleteMutation.isLoading}
          error={deleteMutation.error}
          confirmationButtonColor="error"
        />
      )}
    </Box>
  );
};

export { EventSubscriptionsTab };
